import { AdItemPreview } from '@/components/AdItem';
import { Button } from '@/components/Button';
import { Form } from '@/components/Form';
import { Flex } from '@/components/Grid';
import { Heading } from '@/components/Heading';
import {
  createAd,
  getAd,
  type AdActivityItemType,
  type AdCategoryItemType,
  type AdItemType,
  type AdType,
} from '@/resource/ad';
import { LocationItemType } from '@/resource/location';
import { ProfileItemType } from '@/resource/profile';
import type { ImageUploadType } from '@/util/types';
import { useState } from 'react';
import { CancelLink } from './CancelLink';

type Props = {
  useGeneratedTitle: boolean;
  location: LocationItemType;
  category: AdCategoryItemType | null;
  activities: AdActivityItemType[];
  uploads: ImageUploadType[];
  adType: AdType;
  title: string;
  description: string;
  postalCode: string;
  profile: ProfileItemType;
  onBack: () => void;
  onCreate: (ad: AdItemType) => void;
};

export function PreviewForm({
  location,
  useGeneratedTitle,
  category,
  activities,
  uploads,
  adType,
  title,
  description,
  postalCode,
  profile,
  onBack,
  onCreate,
}: Props) {
  const label = adType === 'offer' ? 'aanbod' : 'vraag';
  const [loading, setLoading] = useState(false);
  const [percentage, setPercentage] = useState(0);

  return (
    <Form
      onSubmit={(e) => {
        e.preventDefault();

        const keywords = activities.map((activity) => activity.name).join(', ');
        const categoryIds = useGeneratedTitle
          ? activities
              .map((activity) => activity.ad_category_id)
              .filter((value, index, self) => self.indexOf(value) === index)
          : category
          ? [category.id]
          : [];

        setLoading(true);
        createAd({
          type: adType,
          title: useGeneratedTitle ? keywords : title,
          description: useGeneratedTitle ? keywords : description,
          location_id: location.id,
          postal_code: postalCode,
          images: uploads.map((image) => image.original),
          crop_rects: uploads.map((image) => image.rect),
          use_generated_title: useGeneratedTitle,
          ad_activity_ids: activities.map(({ id }) => id),
          ad_category_ids: categoryIds,
          onUploadProgress(percentage) {
            setPercentage(percentage);
          },
        })
          .then((res) => {
            if (res.type === 'ok') {
              getAd({ id: res.data.id })
                .then((ad) => onCreate(ad))
                .catch((err) => console.error(err));
            } else {
              console.error(res);
            }
          })
          .catch((err) => console.error(err))
          .finally(() => setLoading(false));
      }}
    >
      <Flex dir="column" gap={4}>
        <Heading visualLevel={3} align="center" balance>
          Zo komt jouw {label} op de website
        </Heading>

        {location != null && (
          <AdItemPreview
            adType={adType}
            title={title}
            description={description}
            profile={profile}
            location={location}
            activities={activities}
            category={category}
            images={uploads.map((image) => image.crop)}
            useGeneratedTitle={useGeneratedTitle}
            percentage={percentage}
            loading={loading}
          />
        )}

        <Flex justify="space-between">
          <Button
            theme="secondary"
            icon="chevron_left_narrow"
            iconSize={2}
            disabled={loading}
            onClick={onBack}
          >
            Terug
          </Button>
          <Button
            type="submit"
            disabled={loading}
            icon="chevron_right_narrow"
            iconPosition="right"
            iconSize={2}
          >
            Plaats {label}
          </Button>
        </Flex>

        <CancelLink confirm adType={adType} />
      </Flex>
    </Form>
  );
}
